/**
 * RequestCard Component
 *
 * Compact card display for feature requests in the Kanban board.
 * Supports drag-and-drop functionality.
 *
 * @module components/dev/RequestCard
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card,
  CardContent,
  CardActions,
  Typography,
  Chip,
  Box,
  IconButton,
  Stack,
} from '@mui/material';
import {
  Visibility as ViewIcon,
  Comment as CommentIcon,
  Image as ImageIcon,
  DragIndicator as DragIcon,
} from '@mui/icons-material';
import {
  FeatureRequest,
  STATUS_BADGES,
  REQUEST_TYPES,
  USER_PRIORITIES,
  DEVELOPER_PRIORITIES,
} from '../../types/featureRequest';
import { formatDistanceToNow } from 'date-fns';

interface RequestCardProps {
  request: FeatureRequest;
  isDragging?: boolean;
}

/**
 * RequestCard - Compact card for Kanban board
 */
const RequestCard: React.FC<RequestCardProps> = ({ request, isDragging = false }) => {
  const navigate = useNavigate();

  const typeConfig = REQUEST_TYPES[request.type];
  const userPriorityConfig = USER_PRIORITIES[request.userPriority];
  const devPriorityConfig = request.developerPriority
    ? DEVELOPER_PRIORITIES[request.developerPriority]
    : null;

  /**
   * Handle view request
   */
  const handleView = (e: React.MouseEvent) => {
    e.stopPropagation();
    navigate(`/dev/feedback/${request.id}`);
  };

  /**
   * Format date
   */
  const formatDate = (dateString: string): string => {
    try {
      return formatDistanceToNow(new Date(dateString), { addSuffix: true });
    } catch {
      return dateString;
    }
  };

  return (
    <Card
      sx={{
        cursor: 'grab',
        opacity: isDragging ? 0.5 : 1,
        '&:hover': {
          boxShadow: 4,
        },
        transition: 'all 0.2s',
      }}
    >
      <CardContent sx={{ pb: 1 }}>
        {/* Drag Handle */}
        <Box display="flex" alignItems="flex-start" gap={1} mb={1}>
          <DragIcon sx={{ color: 'text.secondary', fontSize: 20, mt: 0.5 }} />
          <Box flex={1}>
            {/* Priority Chips */}
            <Stack direction="row" spacing={0.5} mb={1} flexWrap="wrap">
              {/* Type */}
              <Chip
                label={typeConfig.icon}
                size="small"
                sx={{
                  bgcolor: typeConfig.color,
                  color: 'white',
                  height: 20,
                  fontSize: '0.7rem',
                }}
              />

              {/* User Priority */}
              <Chip
                label={`User: ${request.userPriority.toUpperCase()}`}
                size="small"
                sx={{
                  bgcolor: userPriorityConfig.color,
                  color: 'white',
                  height: 20,
                  fontSize: '0.65rem',
                }}
              />

              {/* Developer Priority */}
              {devPriorityConfig && (
                <Chip
                  label={`Dev: ${request.developerPriority?.toUpperCase()}`}
                  size="small"
                  sx={{
                    bgcolor: devPriorityConfig.color,
                    color: 'white',
                    height: 20,
                    fontSize: '0.65rem',
                  }}
                />
              )}
            </Stack>

            {/* Title */}
            <Typography
              variant="subtitle2"
              gutterBottom
              sx={{
                fontWeight: 600,
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
              }}
            >
              {request.title}
            </Typography>

            {/* Description Preview */}
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
              }}
            >
              {request.description}
            </Typography>

            {/* Metadata */}
            <Box mt={1}>
              <Typography variant="caption" color="text.secondary" display="block">
                By: {request.submittedByName || 'Anonymous'}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {formatDate(request.createdAt)}
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Counts */}
        <Stack direction="row" spacing={1} mt={1}>
          {request.screenshotCount > 0 && (
            <Chip
              icon={<ImageIcon />}
              label={request.screenshotCount}
              size="small"
              variant="outlined"
              sx={{ height: 20 }}
            />
          )}
          {request.commentCount > 0 && (
            <Chip
              icon={<CommentIcon />}
              label={request.commentCount}
              size="small"
              variant="outlined"
              color="primary"
              sx={{ height: 20 }}
            />
          )}
        </Stack>
      </CardContent>

      <CardActions sx={{ pt: 0, justifyContent: 'flex-end' }}>
        <IconButton size="small" onClick={handleView}>
          <ViewIcon fontSize="small" />
        </IconButton>
      </CardActions>
    </Card>
  );
};

export default RequestCard;
